import { cn } from '@neo/lib/utils'
import { useEffect, useRef, useState } from 'react'

// A slab of clear glass with liquid light turning inside it, shaped like its rounded parent.
const VERTEX = 'attribute vec2 aPos; void main() { gl_Position = vec4(aPos, 0.0, 1.0); }'

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uSize;
uniform float uRadius;
uniform float uTime;
uniform float uReveal;

float hash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float noise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash(i), hash(i + vec3(1.0, 0.0, 0.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 0.0)), hash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
    mix(mix(hash(i + vec3(0.0, 0.0, 1.0)), hash(i + vec3(1.0, 0.0, 1.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 1.0)), hash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y),
    f.z);
}

float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * noise(p);
    p = p * 2.02 + vec3(17.1, 3.7, 9.3);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 hs = 0.5 * uSize;
  vec2 c = gl_FragCoord.xy - hs;
  // Distance to the rounded edge in pixels, negative inside, and the way out from here.
  vec2 d = abs(c) - (hs - uRadius);
  float sd = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - uRadius;
  vec2 g = (d.x > 0.0 && d.y > 0.0) ? normalize(d) : (d.x > d.y ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  g *= sign(c);
  float alpha = clamp(0.5 - sd / 1.5, 0.0, 1.0);
  // The glass bulges like a pillow: 0 along the middle, 1 at the edge.
  float rr = 1.0 - clamp(-sd / hs.y, 0.0, 1.0);
  float z = sqrt(max(1.0 - rr * rr, 0.0));
  vec3 n = normalize(vec3(g * rr, z));
  vec2 p = c / hs.y;
  float t = uTime * 0.2;

  // Seen through the glass: magnified in the middle, squeezed at the edge.
  vec2 q = p * (1.0 - 0.3 * z);
  vec3 s = vec3(q * 0.6 + vec2(t * 0.25, 0.0), t);
  vec2 w = vec2(fbm(s), fbm(s + vec3(5.2, 1.3, 2.8)));
  float f = 0.5 + 1.2 * (fbm(s + vec3(2.2 * w, 0.0)) - 0.5) + 0.04 - 0.15 * rr * rr;

  // Colour fills the glass, with only small deep pockets. Sharp steps keep the liquid crisp.
  vec3 col = mix(vec3(0.09, 0.05, 0.24), vec3(0.22, 0.12, 0.52), smoothstep(0.34, 0.42, f));
  col = mix(col, vec3(0.40, 0.24, 0.80), smoothstep(0.45, 0.48, f));
  vec3 bloom = mix(vec3(0.83, 0.35, 0.84), vec3(0.76, 0.64, 0.94), smoothstep(0.4, 0.62, w.y));
  col = mix(col, bloom, smoothstep(0.53, 0.555, f));
  float core = smoothstep(0.607, 0.617, f);
  col = mix(col, vec3(0.98, 0.86, 0.76), smoothstep(0.585, 0.6, f) * (1.0 - core) * 0.9);
  vec3 mint = mix(vec3(0.60, 0.88, 0.84), vec3(0.70, 0.70, 0.95), smoothstep(0.4, 0.65, w.x));
  col = mix(col, mix(mint, vec3(0.72, 0.93, 0.90), smoothstep(0.7, 0.85, f)), core);
  float ember = smoothstep(-0.4, -0.9, p.y) * smoothstep(0.45, 0.6, fbm(s * 1.6 + 7.0));
  col = mix(col, vec3(0.70, 0.30, 0.12), ember * 0.8 * (1.0 - core));

  // The glass: darker toward the edge, a faint blue rim, wavy glints and a lit top edge.
  col *= 0.5 + 0.5 * smoothstep(0.0, 0.5, z);
  col += vec3(0.30, 0.32, 0.95) * smoothstep(0.85, 0.97, rr) * (1.0 - smoothstep(0.97, 1.0, rr)) * 0.4;
  float ripple = sin(rr * 22.0 + 7.5 * fbm(vec3(p * 1.2, t * 0.6)));
  float band = smoothstep(0.6, 0.85, rr) * (1.0 - smoothstep(0.93, 0.99, rr));
  float side = 0.3 + 0.7 * smoothstep(-0.2, 0.8, dot(g, vec2(0.75, -0.66)));
  float glint = smoothstep(0.86, 0.99, ripple) * band * side * smoothstep(0.35, 0.6, fbm(vec3(p * 2.0, t + 3.0)));
  col = mix(col, vec3(1.0), glint * 0.8);
  col = mix(col, vec3(1.0), (1.0 - smoothstep(0.0, 0.05, abs(rr - 0.86))) * smoothstep(0.2, 0.9, g.y) * 0.7);
  vec3 light = normalize(vec3(-0.5, 0.6, 0.65));
  col += pow(max(dot(reflect(-light, n), vec3(0.0, 0.0, 1.0)), 0.0), 40.0) * 0.4;

  // Switching on sweeps the glass in from the left edge to the right.
  float reach = (2.0 * uReveal - 1.0) * (hs.x / hs.y + 0.5);
  float shown = 1.0 - smoothstep(reach - 0.5, reach, p.x);
  gl_FragColor = vec4(col, 1.0) * alpha * shown;
}
`

export const LiquidGlass = ({ className }: { className?: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const gl = canvas?.getContext('webgl', { antialias: false })
    if (!canvas || !gl) {
      setFailed(true)
      return
    }
    const lose = () => gl.getExtension('WEBGL_lose_context')?.loseContext()
    const program = gl.createProgram() as WebGLProgram
    for (const [type, source] of [
      [gl.VERTEX_SHADER, VERTEX],
      [gl.FRAGMENT_SHADER, FRAGMENT],
    ] as const) {
      const shader = gl.createShader(type) as WebGLShader
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      gl.attachShader(program, shader)
    }
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      lose()
      setFailed(true)
      return
    }
    gl.useProgram(program)
    // One triangle larger than the canvas covers every pixel.
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, 'aPos')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    const uniform = (name: string) => gl.getUniformLocation(program, name)
    const time = uniform('uTime')
    const reveal = uniform('uReveal')

    // Drawn at twice the screen's pixels and scaled down, so the glass stays sharp at button size.
    const scale = 2 * Math.min(devicePixelRatio || 1, 3)
    const fit = () => {
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale))
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale))
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.uniform2f(uniform('uSize'), canvas.width, canvas.height)
      gl.uniform1f(
        uniform('uRadius'),
        parseFloat(getComputedStyle(canvas).borderTopLeftRadius) * scale,
      )
    }
    const draw = (seconds: number) => {
      gl.uniform1f(reveal, 1 - (1 - Math.min(seconds / 0.35, 1)) ** 2)
      gl.uniform1f(time, 4 + seconds)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    }
    fit()

    let raf = 0
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    const observer = new ResizeObserver(() => {
      fit()
      if (motion.matches) draw(1)
    })
    observer.observe(canvas)
    // A lost context never comes back unless the event is cancelled, so the gradient stands in.
    const onLost = () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      setFailed(true)
    }
    canvas.addEventListener('webglcontextlost', onLost)
    const start = performance.now()
    const frame = (now: number) => {
      // Reduced motion, at mount or switched on later, leaves one still frame.
      if (motion.matches) {
        draw(1)
        return
      }
      draw((now - start) / 1000)
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => {
      canvas.removeEventListener('webglcontextlost', onLost)
      observer.disconnect()
      cancelAnimationFrame(raf)
      // Single-shot: a re-run on this canvas, as Fast Refresh does, gets the lost context back.
      lose()
    }
  }, [])

  if (failed) {
    return (
      <span
        className={cn(
          'bg-[radial-gradient(ellipse_at_50%_50%,#9fe0d8,#7c4fd6_45%,#120a2a_85%)]',
          className,
        )}
      />
    )
  }
  return <canvas ref={canvasRef} className={className} />
}
